/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  chequeNoValidations,
  chequeBSBValidations,
  chequeAccountNoValidations,
  moneyOrderNumberValidations,
  moneyOrderTextValidations,
} from 'linced-app/utils/finance';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class PaymentEditor extends Component {
  @service
  features;

  @service
  finance;

  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @alias('session.school')
  school;

  // NSW DoE prohibited financial language (SMOS-012).
  // Populated by Finance::ProhibitedLanguageAlert via @onChange.
  @tracked
  paymentCommentViolations = [];

  @action
  setPaymentCommentViolations(value) {
    this.paymentCommentViolations = value;
  }

  @computed('paymentCommentViolations.[]')
  get hasLanguageViolations() {
    return !isEmpty(this.paymentCommentViolations);
  }

  @tracked
  paymentComment = '';

  @action
  setPaymentComment(value) {
    this.paymentComment = value;
  }

  @tracked
  paymentDate = '';

  @action
  setPaymentDate(value) {
    this.paymentDate = value;
  }

  @tracked
  paymentMethod = 0;

  @action
  setPaymentMethod(value) {
    this.paymentMethod = value;
  }

  @tracked
  paymentPerson = null;

  @tracked
  paymentPersonName = '';

  @tracked
  paymentChequeNo = '';

  @action
  setPaymentChequeNo(value) {
    this.paymentChequeNo = value;
  }

  @tracked
  paymentChequeBSB = '';

  @action
  setPaymentChequeBSB(value) {
    this.paymentChequeBSB = value;
  }

  @tracked
  paymentChequeAccountNo = '';

  @action
  setPaymentChequeAccountNo(value) {
    this.paymentChequeAccountNo = value;
  }

  @tracked
  paymentMoneyOrderNumber = '';

  @action
  setPaymentMoneyOrderNumber(value) {
    this.paymentMoneyOrderNumber = value;
  }

  @tracked
  paymentMoneyOrderIssuer = '';

  @action
  setPaymentMoneyOrderIssuer(value) {
    this.paymentMoneyOrderIssuer = value;
  }

  @tracked
  paymentMoneyOrderPayee = '';

  @action
  setPaymentMoneyOrderPayee(value) {
    this.paymentMoneyOrderPayee = value;
  }

  @tracked
  changeReason = '';

  @action
  setChangeReason(value) {
    this.changeReason = value;
  }

  @tracked
  saveInProgress = false;

  PaymentMethod = PaymentMethod;

  @alias('finance.availablePaymentMethods')
  paymentMethods;

  chequeNoValidations = chequeNoValidations;
  chequeBSBValidations = chequeBSBValidations;
  chequeAccountNoValidations = chequeAccountNoValidations;
  moneyOrderNumberValidations = moneyOrderNumberValidations;
  moneyOrderTextValidations = moneyOrderTextValidations;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    let paymentPerson;
    if (!isEmpty(this.transaction.person)) {
      // The UI uses the contact records rather than the caregivers directly.
      // Check for a matching Bill Payer contact to preselect.
      let billPayers = this.get(
        'transaction.account.person.learner.billPayers',
      );
      if (!isEmpty(billPayers)) {
        paymentPerson = findBy(
          billPayers,
          'caregiverId',
          this.get('transaction.person.id'),
        );
      }
    }
    this.paymentComment = this.transaction.comment;
    this.paymentDate = this.transaction.date;
    this.paymentMethod = this.transaction.method;
    this.paymentPerson = paymentPerson;
    this.paymentPersonName = this.transaction.paidBy;
    this.paymentChequeNo = this.transaction.chequeNo;
    this.paymentChequeBSB = this.transaction.chequeBSB;
    this.paymentChequeAccountNo = this.transaction.chequeAccountNo;
    this.paymentMoneyOrderNumber = this.transaction.moneyOrderNumber;
    this.paymentMoneyOrderIssuer = this.transaction.moneyOrderIssuer;
    this.paymentMoneyOrderPayee = this.transaction.moneyOrderPayee;
  }

  @action
  setPaidBy(billPayer) {
    this.paymentPerson = billPayer;
    this.paymentPersonName = billPayer.fullName;
  }

  @action
  setPaidByName(name) {
    this.paymentPerson = null;
    this.paymentPersonName = name;
  }

  @action
  async save() {
    this.saveInProgress = true;
    try {
      this.transaction.setProperties({
        comment: this.paymentComment,
        date: this.paymentDate,
        method: this.paymentMethod,
        chequeNo: this.paymentChequeNo,
        chequeBSB: this.paymentChequeBSB,
        chequeAccountNo: this.paymentChequeAccountNo,
        moneyOrderNumber: this.paymentMoneyOrderNumber,
        moneyOrderIssuer: this.paymentMoneyOrderIssuer,
        moneyOrderPayee: this.paymentMoneyOrderPayee,
        auditReason: this.changeReason,
      });
      // Only personId OR personName can be set - not both.
      if (!isEmpty(this.paymentPerson)) {
        this.transaction.setProperties({
          // A belongsTo takes a record. Load the caregiver if necessary.
          personId: await this.store.findRecord(
            'person',
            this.paymentPerson.caregiverId,
            { backgroundReload: false },
          ),
          personName: '',
        });
      } else {
        this.transaction.setProperties({
          personId: null,
          personName: this.paymentPersonName,
        });
      }
      await this.transaction.save();

      // Reload any known child transactions, in case they were updated by this update.
      this.transaction.childTransactions.forEach((childTransaction) => {
        childTransaction.reload();
      });
      this.paperToaster.show(`Payment details updated successfully.`, {
        duration: 4000,
        position: 'top right',
        toastClass: 'success',
      });
      this.onClose();
    } finally {
      // A failed caregiver load or save lets staff retry or close.
      this.saveInProgress = false;
    }
  }
}
